import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  ChevronLeft,
  Menu,
  LogOutIcon,
  UserCircle,
  Calendar,
  ArrowLeftRight,
  CalendarSync,
  Wallet,
  Handshake,
} from "lucide-react";
import Tooltip from "../ui/tooltip.tsx";

type Item = { label: string; to: string; icon: React.ReactNode };

const items: Item[] = [
  {
    label: "Dashboard",
    to: "/dashboard",
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
  {
    label: "Calendar",
    to: "/calendar",
    icon: <Calendar className="w-5 h-5" />,
  },
  {
    label: "Transactions",
    to: "/transactions",
    icon: <ArrowLeftRight className="w-5 h-5" />,
  },
  {
    label: "Budgets",
    to: "/budgets",
    icon: <Wallet className="w-5 h-5" />,
  },
  {
    label: "Subscriptions",
    to: "/subscriptions",
    icon: <CalendarSync className="w-5 h-5" />,
  },
  // { label: "Loans", to: "/loans", icon: <Landmark className="w-5 h-5" /> },
  // { label: "Goals", to: "/goals", icon: <Target className="w-5 h-5" /> },
  { label: "Friends", to: "/friends", icon: <Handshake className="w-5 h-5" /> },
  // { label: "Groups", to: "/group", icon: <Users className="w-5 h-5" /> },
  // { label: "Amortization", to: "/amortization", icon: <Calculator className="w-5 h-5" /> },
];

const storageKey = "sidebar:collapsed";

const SideNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // Always keep desktop sidebar collapsed as per TODO requirement
    const value = true;
    setCollapsed(value);
    localStorage.setItem(storageKey, String(value));
    window.dispatchEvent(
      new CustomEvent("sidebar:toggle", { detail: { collapsed: value } })
    );
  }, []);

  useEffect(() => {
    // close on route change (mobile)
    setMobileOpen(false);
  }, [location.pathname]);

  const logout = () => {
    try {
      localStorage.removeItem("user");
    } catch {}
    navigate("/");
  };

  const Aside = (
    <aside
      className={[
        "flex flex-col h-screen backdrop-blur border-r border-white/10",
        "text-gray-200",
        collapsed ? "w-20" : "w-64",
        "transition-all duration-200",
      ].join(" ")}
    >
      {/* Brand + collapse */}
      <div className="flex items-center justify-between gap-2 h-16 px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-sky-500" />
          {!collapsed && (
            <span className="text-white font-bold tracking-tight">Budiee</span>
          )}
        </div>
        {/* Desktop always-collapsed: remove toggle */}
        {/* Mobile close */}
        <button
          className="md:hidden inline-flex items-center justify-center w-8 h-8 rounded hover:bg-white/10"
          onClick={() => setMobileOpen(false)}
          aria-label="Close"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Nav items */}
      <nav className={["mt-2 px-2 flex-1 overflow-x-visible w-max"].join(" ")}>
        <ul className="space-y-1">
          {items.map((it) => (
            <li key={it.to}>
              <Tooltip content={it.label}>
                <NavLink
                  to={it.to}
                  className={({ isActive }) =>
                    [
                      "group relative w-full flex items-center gap-3 px-3 py-2",
                      "hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400/60",
                      isActive
                        ? "bg-white/10 text-white before:opacity-100"
                        : "text-gray-300",
                      "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-6 before:w-0.5 before:rounded-full before:bg-indigo-400 before:opacity-0 before:transition-opacity",
                    ].join(" ")
                  }
                >
                  <span className="w-8 h-8 rounded-lg bg-white/5 grid place-items-center text-gray-200">
                    {it.icon}
                  </span>
                  <span
                    className={[
                      "whitespace-nowrap transition-opacity",
                      collapsed
                        ? "hidden opacity-0 pointer-events-none"
                        : "opacity-100",
                    ].join(" ")}
                  >
                    {it.label}
                  </span>
                </NavLink>
              </Tooltip>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom actions */}
      <div className="px-2 py-3 border-t border-white/10">
        <Tooltip content="Profile">
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              [
                "group relative w-full flex items-center px-3 py-2",
                "hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400/60",
                isActive
                  ? "bg-white/10 text-white before:opacity-100"
                  : "text-gray-300",
                "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-6 before:w-0.5 before:rounded-full before:bg-indigo-400 before:opacity-0 before:transition-opacity",
              ].join(" ")
            }
          >
            <span className="w-8 h-8 rounded-lg bg-white/5 grid place-items-center text-gray-200">
              <UserCircle className="w-5 h-5" />
            </span>
          </NavLink>
        </Tooltip>
        <Tooltip content="Settings">
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              [
                "group relative w-full flex items-center px-3 py-2",
                "hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400/60",
                isActive
                  ? "bg-white/10 text-white before:opacity-100"
                  : "text-gray-300",
                "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-6 before:w-0.5 before:rounded-full before:bg-indigo-400 before:opacity-0 before:transition-opacity",
              ].join(" ")
            }
          >
            <span className="w-8 h-8 rounded-lg bg-white/5 grid place-items-center text-gray-200">
              <Settings className="w-5 h-5" />
            </span>
          </NavLink>
        </Tooltip>
        <Tooltip content="Logout">
          <button
            className="w-full inline-flex items-center px-3 py-2 hover:bg-white/10 text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400/60"
            onClick={logout}
          >
            <span className="w-8 h-8 rounded-lg bg-white/5 grid place-items-center text-gray-200">
              <LogOutIcon className="w-5 h-5" />
            </span>
          </button>
        </Tooltip>
      </div>
    </aside>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        className="md:hidden fixed bottom-5 left-5 z-40 inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-sky-500 text-white shadow-lg"
        onClick={() => setMobileOpen(true)}
        aria-label="Open sidebar"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Desktop sidebar */}
      <div className="hidden md:block fixed left-0 top-0 z-40">{Aside}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden">
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed left-0 top-0 z-50 w-64 h-screen">{Aside}</div>
        </div>
      )}
    </>
  );
};

export default SideNav;

